import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useDesign } from '../../contexts/DesignContext';

// Todoist-style priority colors: high = red, medium = orange, low = blue
const PRIORITY_STYLES = {
  high: 'border-[1.5px] border-red-500 dark:border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 data-[state=checked]:text-white',
  medium: 'border-[1.5px] border-orange-400 dark:border-orange-400 data-[state=checked]:bg-orange-400 data-[state=checked]:border-orange-400 data-[state=checked]:text-white',
  low: 'border-[1.5px] border-blue-500 dark:border-blue-400 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:text-white',
};

export const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

/**
 * Sort comparator: overdue first, then priority (high→low; none/unset last),
 * then time. Call sites handle completed-task placement themselves.
 */
export function comparePriorityTasks(a, b) {
  const aOver = !!a.isOverdue && !a.completed;
  const bOver = !!b.isOverdue && !b.completed;
  if (aOver !== bOver) return aOver ? -1 : 1;
  const rank = (t) => PRIORITY_RANK[t.priority ?? t.priority_level] ?? 3;
  const pr = rank(a) - rank(b);
  if (pr !== 0) return pr;
  if (!a.time && !b.time) return 0;
  if (!a.time) return 1;
  if (!b.time) return -1;
  return a.time.localeCompare(b.time);
}

/** Checkbox whose outline (and checked fill) is colored by task priority.
 *  Under Studio the box stays neutral and the priority reads as a dot beside
 *  the label instead (101 §6); `data-priority` is the hook the CSS layer uses,
 *  and it is inert in the classic look. */
export function PriorityCheckbox({ priority, className = '', ...props }) {
  const { isStudio } = useDesign();
  if (isStudio) {
    return (
      <span className="inline-flex items-center gap-2 flex-shrink-0">
        <Checkbox data-priority={priority || undefined} className={className} {...props} />
        {priority ? (
          <span className="studio-dot" data-priority-dot={priority} aria-hidden="true" />
        ) : null}
      </span>
    );
  }
  return (
    <Checkbox
      data-priority={priority || undefined}
      className={cn(className, PRIORITY_STYLES[priority])}
      {...props}
    />
  );
}

export default PriorityCheckbox;
